import type { ReactNode } from "react";
import { NumeralDisc } from "@/components/ui/numeral-disc";
import { INSTALL_STEPS, type InstallStepsVariant } from "@/lib/install-steps";
import { cn } from "@/lib/utils";

interface InstallStepsProps {
  /** `condensed` is the three-step summary, `expanded` the four-step form. */
  variant?: InstallStepsVariant;
  /** `rows` stacks the steps; `columns` sets them side by side from sm. Both show every word. */
  layout?: "rows" | "columns";
  /** Extra detail under a step, keyed by its 1-based number (rows only). */
  notes?: Partial<Record<number, ReactNode>>;
  className?: string;
}

/**
 * The install path with §02.7 disc numerals. Copy comes only from
 * lib/install-steps.ts, so every surface that renders this agrees on step
 * count, order and wording (ANTI_SLOP rule 4).
 */
export function InstallSteps({ variant = "expanded", layout = "rows", notes, className }: InstallStepsProps) {
  const steps = INSTALL_STEPS[variant];

  if (layout === "columns") {
    return (
      // list-style: none drops list semantics in Safari; role="list" keeps them.
      // eslint-disable-next-line jsx-a11y/no-redundant-roles
      <ol role="list" className={cn("m-0 grid list-none gap-x-6 gap-y-4 p-0 sm:grid-cols-3", className)}>
        {steps.map((step, i) => (
          <li key={step.title} className="flex gap-3">
            <NumeralDisc n={i + 1} d={32} active />
            <div className="min-w-0">
              <p className="m-0 flex min-h-8 items-center text-sm font-semibold text-[var(--tls-fg-strong)]">{step.title}</p>
              <p className="m-0 mt-1 text-sm leading-relaxed text-[var(--tls-fg-muted)]">{step.detail}</p>
            </div>
          </li>
        ))}
      </ol>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <ol role="list" className={cn("m-0 list-none p-0", className)}>
      {steps.map((step, i) => (
        <li key={step.title} className="flex gap-4 border-t border-[color:var(--tls-border-subtle)] py-4">
          <NumeralDisc n={i + 1} d={32} active />
          <div className="min-w-0 flex-1">
            <p className="m-0 flex min-h-8 items-center font-semibold text-[var(--tls-fg-strong)]">{step.title}</p>
            <p className="m-0 mt-1 text-sm leading-relaxed text-[var(--tls-fg-muted)]">{step.detail}</p>
            {notes?.[i + 1] && <div className="mt-3 text-sm leading-relaxed text-[var(--tls-fg-subtle)]">{notes[i + 1]}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}
